import { inject } from '@angular/core';
import { CanActivateFn, Params, Router, UrlTree } from '@angular/router';
import { AppRoutes } from '../const/AppRoutes';
import { AppRoutesName } from '../const/AppRoutesName';
import { JwtInterceptor } from '../interceptors/JwtInterceptor';

/**
 * URLs sans le nom du tenant (ex. https://g-t.app/configuration/preferences-courriel).
 *
 * Toutes les routes de l'application sont écrites `:shop_uuid/...`, mais le tenant réel
 * vient du localStorage (`PREFIX`, envoyé en `App-Tenant`) : le segment d'URL n'est jamais
 * vérifié. Une adresse partagée sans slug (notes de mise à jour, support) est donc
 * réécrite en `/{PREFIX}/{même chemin}` pour l'utilisateur connecté, et rejouée après la
 * connexion pour l'utilisateur déconnecté (`JwtInterceptor.LAST_URL_401`, consommé par
 * `redirectToLandingPage()` du login).
 *
 * Deux points d'entrée :
 * - `tenantPrefixGuard` sur la route `**` d'app.module.ts (URL qui ne matche aucune route) ;
 * - `rewriteUrlWithTenantPrefix()` au `NavigationStart` d'app.component.ts, pour les URLs à
 *   deux segments qui matchent par accident `:shop_uuid/<page>` (ex. `/configuration/rapports`
 *   ouvrirait la page Rapports avec « configuration » comme tenant).
 */

/** Premiers segments des routes qui existent sans slug : jamais réécrits. */
const SLUGLESS_FIRST_SEGMENTS: ReadonlySet<string> = new Set(
  [
    AppRoutesName.FR.LOGIN,
    AppRoutesName.EN.LOGIN,
    AppRoutesName.FR.FORGOT_PASSWORD,
    AppRoutesName.EN.FORGOT_PASSWORD,
    AppRoutesName.FR.RELEASE_NOTES,
    AppRoutesName.EN.RELEASE_NOTES,
    AppRoutesName.FR.MAINTENANCE,
    AppRoutesName.EN.MAINTENANCE,
    AppRoutesName.FR.ERROR_404,
    AppRoutesName.FR.ERROR_500,
    AppRoutesName.FR.ERROR_503,
    AppRoutesName.FR.ERROR_429,
    AppRoutesName.EN.ERROR_404,
    AppRoutesName.EN.ERROR_500,
    AppRoutesName.EN.ERROR_503,
    AppRoutesName.EN.ERROR_429,
  ].map((route) => firstSegment(route)),
);

function firstSegment(url: string): string {
  return url.replace(/^\/+/, '').split(/[/?#]/)[0] || '';
}

function storedPrefix(): string {
  return (localStorage.getItem('PREFIX') || '').replace(/^\/+|\/+$/g, '');
}

function isAuthenticated(): boolean {
  return !!localStorage.getItem('id_token');
}

/**
 * URL réécrite avec le tenant mémorisé, ou null si l'URL doit rester telle quelle
 * (déconnecté, pas de tenant, route sans slug, ou déjà préfixée).
 */
export function rewriteUrlWithTenantPrefix(url: string): string | null {
  if (!isAuthenticated()) {
    return null;
  }
  const prefix = storedPrefix();
  const first = firstSegment(url);
  if (
    !prefix ||
    !first ||
    first === prefix ||
    SLUGLESS_FIRST_SEGMENTS.has(first)
  ) {
    return null;
  }
  return '/' + prefix + '/' + url.replace(/^\/+/, '');
}

/**
 * Paramètres de connexion à transmettre au login (`?username=…&logout=true`, ex.
 * https://g-t.app?username=a@b.com&logout=true) ; vide s'il n'y en a aucun.
 */
function loginQueryParams(queryParams: Params): Params {
  const params: Params = {};
  if (queryParams['username']) {
    params['username'] = queryParams['username'];
  }
  if (queryParams['logout'] === 'true') {
    params['logout'] = 'true';
  }
  return params;
}

/** Garde de la route `**` : réécrit l'URL avec le tenant, ou mémorise l'URL puis va au login. */
export const tenantPrefixGuard: CanActivateFn = (route, state): UrlTree => {
  const router = inject(Router);
  const url = state.url || '/';
  const first = firstSegment(url);
  const loginParams = loginQueryParams(route.queryParams);

  if (!isAuthenticated()) {
    if (first && !SLUGLESS_FIRST_SEGMENTS.has(first)) {
      JwtInterceptor.LAST_URL_401 = url;
    }
    return router.createUrlTree(['/' + AppRoutes.LOGIN], {
      queryParams: loginParams,
    });
  }

  // Connecté + `?logout=true` : le login déconnecte puis pré-remplit le courriel.
  if (loginParams['logout']) {
    return router.createUrlTree(['/' + AppRoutes.LOGIN], {
      queryParams: loginParams,
    });
  }

  const rewritten = rewriteUrlWithTenantPrefix(url);
  if (rewritten) {
    return router.parseUrl(rewritten);
  }

  // Déjà préfixée (page inconnue) ou sans tenant mémorisé : tableau de bord, comme avant.
  const prefix = storedPrefix();
  return router.parseUrl(
    '/' + (prefix ? prefix + '/' : '') + AppRoutes.EVENTS.replace(/^\/+/, ''),
  );
};
